'use client';

import { createClient } from '@/lib/supabase/client';
import { disablePush } from '@/lib/native/push';

/**
 * Clears the local session and hard-navigates — to /login by default, or to
 * `redirectTo` (account deletion sends the former user to the public
 * marketplace, since there is no account left to sign back into).
 *
 * Shared so every entry point clears the *same* things. The stored business id
 * in particular doubles as the sidebar's signed-in signal, so a sign-out that
 * leaves it behind keeps showing app nav on public routes until the first 401
 * round-trip clears it.
 */
export async function signOut(redirectTo = '/login'): Promise<void> {
  // First, while the access token is still valid: drop this device's push
  // registration. Leaving it behind would send the next person to sign in on
  // this phone the previous account's offers and messages. Bounded, because a
  // slow network must not hold the sign-out hostage — the server retires the
  // token anyway the first time APNs reports it gone.
  await Promise.race([
    disablePush(),
    new Promise((resolve) => setTimeout(resolve, 2000)),
  ]).catch(() => undefined);

  localStorage.removeItem('sweetspot_current_business_id');
  document.cookie = 'sweetspot_dev_auth=;path=/;max-age=0';
  document.cookie.split(';').forEach((entry) => {
    const name = entry.trim().split('=')[0];
    if (name.startsWith('sb-')) {
      document.cookie = `${name}=;path=/;max-age=0`;
    }
  });

  try {
    // A hung network call must not strand the user on a half-signed-out UI —
    // the cookies are already gone by this point either way.
    await Promise.race([
      createClient().auth.signOut(),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
  } catch {
    // Ignore sign out errors and continue redirecting to login.
  }

  window.location.href = redirectTo;
}
